// src/components/agencies/OpeningHoursManager.jsx
import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateOpeningHours } from '../../store/actions/agencyActions';

const OpeningHoursManager = ({ agencyId, currentHours = {}, onClose, onUpdate }) => {
  const dispatch = useDispatch();
  const [hours, setHours] = useState({
    monday: currentHours?.monday || '09:00 - 18:00',
    tuesday: currentHours?.tuesday || '09:00 - 18:00',
    wednesday: currentHours?.wednesday || '09:00 - 18:00',
    thursday: currentHours?.thursday || '09:00 - 18:00',
    friday: currentHours?.friday || '09:00 - 18:00',
    saturday: currentHours?.saturday || 'Fermé',
    sunday: currentHours?.sunday || 'Fermé',
  });
  const [loading, setLoading] = useState(false);

  const days = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' },
  ];

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '80vh',
      overflow: 'hidden',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
      padding: '1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#9ca3af',
    },
    content: {
      padding: '1.5rem',
      maxHeight: 'calc(80vh - 130px)',
      overflowY: 'auto',
    },
    hoursGrid: {
      display: 'grid',
      gap: '1rem',
    },
    hourRow: {
      display: 'grid',
      gridTemplateColumns: '100px 1fr',
      gap: '1rem',
      alignItems: 'center',
    },
    dayLabel: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    inputGroup: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    input: {
      flex: 1,
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
    },
    closedCheckbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    footer: {
      padding: '1.5rem',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    button: {
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s',
    },
    cancelButton: {
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    saveButton: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
    },
    saveButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    saveButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  const handleHourChange = (day, value) => {
    setHours(prev => ({
      ...prev,
      [day]: value,
    }));
  };

  const handleClosedToggle = (day, isClosed) => {
    setHours(prev => ({
      ...prev,
      [day]: isClosed ? 'Fermé' : '09:00 - 18:00',
    }));
  };

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(updateOpeningHours(agencyId, hours));
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to update opening hours:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, agencyId, hours, onUpdate, onClose]);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Gérer les horaires d'ouverture</h3>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.content}>
          <div style={styles.hoursGrid}>
            {days.map(({ key, label }) => (
              <div key={key} style={styles.hourRow}>
                <div style={styles.dayLabel}>{label}</div>
                <div style={styles.inputGroup}>
                  <input
                    type="text"
                    value={hours[key] === 'Fermé' ? '' : hours[key]}
                    onChange={(e) => handleHourChange(key, e.target.value)}
                    disabled={hours[key] === 'Fermé'}
                    placeholder="ex: 09:00 - 18:00"
                    style={styles.input}
                  />
                  <label style={styles.closedCheckbox}>
                    <input
                      type="checkbox"
                      checked={hours[key] === 'Fermé'}
                      onChange={(e) => handleClosedToggle(key, e.target.checked)}
                      style={styles.checkbox}
                    />
                    <span style={styles.checkboxLabel}>Fermé</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.footer}>
          <button
            onClick={onClose}
            style={styles.button}
            css={[styles.cancelButton]}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              ...styles.button,
              ...styles.saveButton,
              ...(loading ? styles.saveButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.saveButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.saveButton.backgroundColor;
              }
            }}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpeningHoursManager;